import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import api from '../services/api';

// Formata data/hora ISO para exibição amigável em pt-BR.
const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Popup de notificações exibido no login.
 * Componente auto-contido (sem props): busca as notificações ativas (não lidas)
 * do usuário autenticado e exibe uma por vez. Ao clicar em "Entendi", marca a
 * notificação como lida e avança para a próxima da fila (ou fecha o modal).
 * Falhas de rede são tratadas silenciosamente para não atrapalhar o usuário.
 */
const NotificationPopup = () => {
  const [queue, setQueue] = useState([]); // fila de notificações pendentes
  const [index, setIndex] = useState(0);  // índice da notificação exibida
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/notifications/active/');
        const items = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        if (items.length > 0) {
          setQueue(items);
          setIndex(0);
          setVisible(true);
        }
      } catch (err) {
        // Falha silenciosa: não exibe nada nem interrompe o fluxo do usuário
        console.error('Erro ao carregar notificações', err);
      }
    };
    fetchNotifications();
  }, []);

  const current = queue[index];

  const handleDismiss = async () => {
    // Marca como lida no backend (falha não impede o avanço da fila)
    try {
      await api.post(`/notifications/${current.id}/read/`);
    } catch (err) {
      console.error('Erro ao marcar notificação como lida', err);
    }

    if (index + 1 < queue.length) {
      setIndex(index + 1);
    } else {
      setVisible(false);
    }
  };

  if (!visible || !current) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <Bell size={20} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {current.title}
            </h2>
            {current.created_at && (
              <p className="text-xs text-gray-400 mt-0.5">
                {formatDateTime(current.created_at)}
              </p>
            )}
            {/* Mensagem renderizada como texto puro (pre-line preserva quebras de linha)
                — nunca injeta HTML cru, evitando XSS. */}
            <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">
              {current.message}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          {queue.length > 1 && (
            <span className="text-xs text-gray-400 mr-auto">
              {index + 1} de {queue.length}
            </span>
          )}
          <button
            onClick={handleDismiss}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPopup;
